<template>
  <div class="eapps-main-dashboard">
    <PanelRow>
      <Panel class="logins two-thirds">
        <template slot="header">
          <h2>{{ "dashboard.logins.title" | trans }}</h2>
          <ButtonSwitch class="logins-view" v-model="loginsView" :options=viewOptions />
        </template>
        <div class="chart-holder">
          <LineChart
            :data="logins.slice().reverse()"
            :axis="axis.slice().reverse()"
            :opts="chartOpts.chart"
            :setopts="chartOpts.set"
          />
        </div>
      </Panel>
      <Panel class="third-keep numberstats">
        <template v-if="newLogins">
          <h2>{{ "users:daily_logins" | trans }}</h2>
          <Trend :data=newLogins />
        </template>
        <template v-if="newUsers">
          <h2>{{ "users:daily_new" | trans }}</h2>
          <Trend :data=newUsers />
        </template>
      </Panel>
      <Panel class="third">
        <h2 slot="header">{{ "billings:title" | trans }}</h2>
        <Unpaid />
        <Information v-if="billingPlan" :item="billingPlan" :fields="billingFields" />
        <CreditsUsage :billing=current_billing />
      </Panel>
      <Panel class="two-thirds"></Panel>
    </PanelRow>
  </div>
</template>

<script>
import Recordset from '@/providers/api/Recordset'
import LineChart from '@/helpers/components/charts/LineChart';
import Trend from '@/helpers/components/charts/Trend';
import ButtonSwitch from '@/helpers/components/interact/ButtonSwitch';
import Item from '@/helpers/components/display/Item';
import CreditsUsage from '@/sections/billings/components/CreditsUsage'
import Unpaid from '@/sections/billings/components/Unpaid'
import {format, getDaysInMonth, subDays, subMonths, subHours, startOfMonth, endOfMonth} from 'date-fns'
import {billings_plans as billing_fields} from '@/sections/billings/fields'
import _ from 'lodash';

export default {
  name: 'Dashboard',
  components: {LineChart, ButtonSwitch, Information: Item, Trend, CreditsUsage, Unpaid},
  data () {return {
    yearLogins: new Array(12).fill(undefined),
    monthLogins: new Array(getDaysInMonth(new Date())-1).fill(undefined),
    dayLogins: new Array(24).fill(undefined),
    progress: [0, 0],
    cancel: {},
    loginsView: 'year',
    chartOpts: {
      chart: {
        layout: {
          padding: {
            top: 10,
            bottom: 10,
          }
        },
        scales: {
          xAxes: [{
            ticks: {padding: 8}
          }],
          yAxes: [{
            ticks: {padding: 8}
          }],
        }
      },
      set: {
        borderWidth: 2,
        pointBorderWidth: 8,
        pointHitRadius: 15,
      },
    },
    viewOptions: {
      year: this.$i18n.t('dashboard.logins.year'),
      month: this.$i18n.t('dashboard.logins.month'),
      day: this.$i18n.t('dashboard.logins.day'),
    },
  }},
  computed: {
    logins () {
      switch (this.loginsView) {
        case 'year':
          return this.loadYearLogins();
        case 'month':
          return this.loadMonthLogins();
        case 'day':
          return this.loadDayLogins();
      }
    },
    newLogins () {
      var dayLogins = this.loadMonthLogins([0, 1]);
      if (dayLogins[0] === null || dayLogins[1] === null) {
        return false;
      }
      return dayLogins.slice(0, 2).reverse();
    },
    axis () {
      switch (this.loginsView) {
        case 'year':
          return this.yearLogins.map((v, i) => format(subMonths(new Date(), i), 'MMM'));
        case 'month':
          return this.monthLogins.map((v, i) => format(subDays(new Date(), i), 'DD.MM'));
        case 'day':
          return this.dayLogins.map((v, i) => format(subHours(new Date(), i), 'HH:[00]'));
      }
    },
  },
  asyncComputed: {
    billingFields () {
      return this.async('billing_plan', plan => {
        var keep = ['name'];
        keep.push('prices_subscription_' + plan.billings_cycles);
        return _.pick(billing_fields, keep);
      });
    },
    billingPlan () {
      return this.async('billing_plan', plan => plan.billings_plans);
    },
    current_billing () {
      var now = new Date();
      var start_month = format(startOfMonth(now));
      var end_month = format(endOfMonth(now));
      return this.async(['apptoken', 'billing_plan'])
        .then(([token, plan]) => this.api.get(
          'billings' +
            '?include=billings_contracts' +
            '&range=single' +
            '&filter=apptokens_id::eq::' + token.id +
            ',target_time_resource_query_start::mreq::' + start_month +
            ',target_time_resource_query_start::lseq::' + end_month +
            ',target_time_resource_query_end::mreq::' + start_month +
            ',target_time_resource_query_end::lseq::' + end_month +
          '',
          this.cancel
        ))
      ;
    },
    newUsers () {
      var yesterday = format(subDays(new Date(), 1), DATE_FORMAT);
      var beforeyesterday = format(subDays(new Date(), 2), DATE_FORMAT);
      return Promise.all([
        //get new users from day before yesterday to yesterday
        this.async(
          'apptoken',
          apptoken => this.api.get(
            'apptokens/' + apptoken.id + '/users' +
            '?filter=users_authorized_apptokens.created_at::mr::' + beforeyesterday +
                   ',users_authorized_apptokens.created_at::lseq::' + yesterday +
            '&range=0,0',
            this.cancel
          ).then(() => this.api.lastCount)
        ),
        //get new users from yesterday to today
        this.async(
          'apptoken',
          apptoken => this.api.get(
            'apptokens/' + apptoken.id + '/users' +
            '?filter=users_authorized_apptokens.created_at::mr::' + yesterday +
            '&range=0,0',
            this.cancel
          ).then(() => this.api.lastCount)
        ),
      ]);
    },
  },
  beforeDestroy () {
    if (this.cancel.function) {
      this.cancel.function();
    }
  },
  methods: {
    _countLogins (start, end) {
      return this.api.get(
        'users_logins?' +
        'filter=created_at::mreq::' + start + ',' +
        'created_at::lseq::' + end + '&' +
        'range=0,0',
        this.cancel
      )
      .then(() => this.api.lastCount || 0);
    },
    loadYearLogins (partial = false) {
      this.yearLogins.map((v, i) => {
        if (partial && partial.indexOf(i) === -1) {return;}
        if (v === undefined) {
          this.$set(this.yearLogins, i, null);
          var month = subMonths(new Date(), i);
          this._countLogins(
            format(month, 'YYYY-MM-[01 00:00:00]'),
            format(month, 'YYYY-MM-[' + getDaysInMonth(month) + ' 23:59:59]')
          ).then(c => this.$set(this.yearLogins, i, c))
        }
      });
      return this.yearLogins;
    },
    loadMonthLogins (partial = false) {
      this.monthLogins.map((v, i) => {
        if (partial && partial.indexOf(i) === -1) {return;}
        if (v === undefined) {
          this.$set(this.monthLogins, i, null);
          this._countLogins(
            format(subDays(new Date(), i), 'YYYY-MM-DD [00:00:00]'),
            format(subDays(new Date(), i), 'YYYY-MM-DD [23:59:59]')
          ).then(c => this.$set(this.monthLogins, i, c))
        }
      });
      return this.monthLogins;
    },
    loadDayLogins (partial = false) {
      this.dayLogins.map((v, i) => {
        if (partial && partial.indexOf(i) === -1) {return;}
        if (v === undefined) {
          this.$set(this.dayLogins, i, null);
          this._countLogins(
            format(subHours(new Date(), i), 'YYYY-MM-DD HH:[00:00]'),
            format(subHours(new Date(), i), 'YYYY-MM-DD HH:[59:59]')
          ).then(c => this.$set(this.dayLogins, i, c))
        }
      });
      return this.dayLogins;
    },
  },
}
</script>

<style lang="scss">
  .eapps-main-dashboard {
    .numberstats {
      h2 {
        text-align: center;
        padding: 27px 0 17px;
        &:first-of-type {
          padding-top: 0;
        }
        @include font-size(22px);
      }
    }
    .chart-holder {
      position: relative;
      height: calc(100% - #{50px});
      @media (max-width: $xs-max) {
        height: calc(100% - #{65px});
      }
    }
    .enkronos-credits-usage {
      margin-top: 20px;
      h4 {
        text-align: left;
        color: $default-color;
      }
    }
    .logins {
      > header {
        > h2 {
          float: left;
        }
        > fieldset {
          float: right;
        }
      }
      height: 60vh;
      min-height: 400px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/sections/general/Dashboard.vue